import * as React from 'react'

import { Slot } from '@radix-ui/react-slot'
import { type VariantProps, tv } from 'tailwind-variants'

import LoadingIcon from '@wwt/shared/assets/icons/outline/loading.svg'

// eslint-disable-next-line react-refresh/only-export-components
export const buttonVariants = tv({
	base: 'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-base font-semibold transition-colors focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:cursor-not-allowed disabled:border-border disabled:bg-muted disabled:text-muted-foreground data-disabled:pointer-events-none data-disabled:cursor-not-allowed data-disabled:border-border data-disabled:bg-muted data-disabled:text-muted-foreground [&_svg]:pointer-events-none [&_svg]:size-6 [&_svg]:shrink-0',
	variants: {
		variant: {
			default:
				'bg-primary text-primary-foreground hover:bg-primary-muted active:bg-primary-accent data-loading:bg-primary data-loading:text-primary-foreground',
			destructive:
				'cursor-not-allowed bg-destructive text-destructive-foreground data-loading:bg-destructive data-loading:text-destructive-foreground',
			outline:
				'border border-input bg-background text-foreground hover:border-primary-accent hover:text-primary-accent active:border-transparent active:bg-primary-accent active:text-primary-foreground data-loading:border-input data-loading:bg-background data-loading:text-foreground',
			secondary:
				'border border-primary bg-background text-primary hover:border-primary-accent hover:text-primary-accent active:border-transparent active:bg-primary-accent active:text-primary-foreground data-loading:border-primary data-loading:bg-background data-loading:text-primary',
			tertiary:
				'bg-background text-foreground hover:text-primary-accent active:border-transparent active:bg-primary-accent active:text-primary-foreground disabled:bg-transparent data-disabled:bg-transparent data-loading:bg-background data-loading:text-foreground',
			flat: 'text-foreground hover:text-primary active:text-primary-accent data-disabled:bg-transparent disabled:bg-transparent disabled:text-input data-loading:text-foreground',
			ghost:
				'bg-background text-foreground hover:bg-primary-muted hover:text-primary-foreground active:bg-primary-accent active:text-primary-foreground',
			link: 'text-secondary underline decoration-2 hover:text-muted-foreground active:text-foreground data-loading:bg-transparent data-loading:text-secondary',
			unstyled: ''
		},
		size: {
			default: 'h-12 px-3 py-3',
			sm: 'h-10 px-2.5 py-2',
			lg: 'h-16 px-3.5 py-5',
			icon: 'h-12 w-12',
			'icon-lg': 'h-16 w-16 [&_svg]:size-9',
			'icon-sm': 'h-10 w-10',
			'icon-xs': 'h-6 w-6'
		}
	},
	defaultVariants: {
		variant: 'default',
		size: 'default'
	}
})

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	ref?: React.RefObject<HTMLButtonElement | null>
	asChild?: boolean
	isLoading?: boolean
	loadingText?: React.ReactNode
}

export const Button = ({
	ref,
	className,
	variant,
	size,
	isLoading,
	loadingText,
	children,
	asChild = false,
	...props
}: ButtonProps) => {
	const Comp = asChild ? Slot : 'button'

	const content = isLoading ? (
		<>
			<LoadingIcon className="animate-spin" />
			{loadingText}
		</>
	) : (
		children
	)

	return (
		<Comp
			ref={ref}
			className={buttonVariants({ variant, size, className })}
			type="button"
			data-loading={isLoading || undefined}
			{...props}
		>
			{content}
		</Comp>
	)
}
Button.displayName = 'Button'
